/**
 * Launcher V2 PR C — the sender picker's vocabulary and validation, as pure ops.
 *
 * DOM-free on purpose, the same reason `sequence-readiness.ts` /
 * `playbook-draft.ts` / `review-gate.ts` are: `bun:test` has no DOM under
 * `mini-apps/`, so logic that lives inside a Glaze-importing `.tsx` file is
 * untestable there. The React half is
 * `components/play-flow/SenderChoiceFields.tsx`, which owns nothing but the two
 * form controls and renders whatever this module says.
 *
 * ── WHY THE FORMAT CHECK LIVES HERE, NOT IN THE SCHEMA ────────────────────
 * `SequenceMappingSchema.sender` is a free `z.string()` deliberately —
 * its own doc comment reserves the vocabulary for this module, and enumerating it
 * in the schema would turn every future sender token into a
 * `db_json_schema_mismatch` on already-persisted rows. That is the right call
 * for the DB boundary, and it means the UI is the only place a "this is supposed
 * to be an email address" check can exist.
 *
 * Codex adversarial review found there wasn't one: the old sender-editing
 * modal's (`SenderModal`, deleted PR C / U8 — replaced by an inline popover in
 * `SequencesStep.tsx`) custom Save button only checked
 * `resolvedSender.length > 0`, and its `type="email"` input sits in no
 * `<form>`, so the browser's native constraint validation never fires either.
 * `not-an-email` saved straight through to `mapping.sender`.
 *
 * ── THE ONLY CALLER IS THE INLINE SENDER POPOVER (QA-fixes E2c) ───────────
 * Through PR C, mapping a rung to a sequence collected a sender in the same
 * breath (`SequenceMappingModal`, since `sender` was required-non-empty), so
 * this module was shared between that modal and `SequencesStep.tsx`'s inline
 * popover. Plan Decision 10 pulled sender collection out of the "Change
 * sequence" drawer entirely — it's now `InlineSenderPopover`'s job alone,
 * which is why `SequenceMappingSchema.sender` no longer requires a value.
 */

import { SENDER_TOKENS } from '../_shared/launcher-copy-tokens';
import type { SequenceMapping } from './schemas';

/** The non-token option: "type an address instead of using an owner token". */
export const STATIC_EMAIL_SENDER_OPTION = 'static_email' as const;

export type SenderOption =
  | (typeof SENDER_TOKENS)[number]['value']
  | typeof STATIC_EMAIL_SENDER_OPTION;

/** What the two form controls hold together. `email` is ignored unless `option` is the static one. */
export interface SenderChoice {
  option: SenderOption;
  email: string;
}

const SENDER_TOKEN_VALUES: ReadonlySet<string> = new Set(SENDER_TOKENS.map((token) => token.value));

/** Whether `value` is one of v1's owner tokens (as opposed to a literal address). */
export function isSenderToken(value: string): boolean {
  return SENDER_TOKEN_VALUES.has(value);
}

/**
 * A deliberately BASIC shape check: one `@`, something either side, and a dot in
 * the domain. Not a library, not RFC 5322 — the goal is to catch the typo class
 * the review found (`not-an-email`, `spencer@`, `spencer@clay`), and a stricter
 * regex would start rejecting addresses that genuinely deliver. The real
 * authority on whether an address works is the send itself.
 */
const SENDER_EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function isValidSenderEmail(value: string): boolean {
  return SENDER_EMAIL_PATTERN.test(value);
}

/**
 * Seeds the controls from a rung's current `mapping.sender`.
 *
 * `null` (no sender yet) seeds the STATIC-EMAIL option with an empty field
 * rather than pre-selecting a token: which sender to send as is the rep's
 * decision, and defaulting it would be a hardcoded fallback for exactly the
 * choice this modal exists to capture.
 */
export function senderChoiceFromSender(sender: string | null): SenderChoice {
  if (sender !== null && isSenderToken(sender)) {
    return { option: sender as SenderOption, email: '' };
  }
  return { option: STATIC_EMAIL_SENDER_OPTION, email: sender ?? '' };
}

const SENDER_LABEL_BY_VALUE: Record<string, string> = Object.fromEntries(
  SENDER_TOKENS.map((token) => [token.value, token.label]),
);

/**
 * A sender's display label: an owner token's human label, or a literal address
 * verbatim. `''` and `null` both read "Not set".
 *
 * Playbooks QA round 4, U9 (SN1) — MOVED here from `SequencesStep.tsx`, where
 * it was module-local. Sender now has two display surfaces, not one: the rung
 * header's read-only `Sender: X` line AND Config → Sequences' own row +
 * sender popover. Two copies of a label map is exactly how the rung header and
 * the library row would come to disagree about what `gtme_owner_email` is
 * called.
 *
 * `null` is accepted (not just `''`) because `playbooks_sequence
 * .default_sender` is genuinely nullable, while `mapping.sender` is a
 * non-nullable `z.string()` that spells "unset" as `''` — one function, both
 * spellings, no caller-side normalization.
 */
export function senderLabel(sender: string | null): string {
  if (sender === null || sender === '') return 'Not set';
  return SENDER_LABEL_BY_VALUE[sender] ?? sender;
}

/**
 * Playbooks QA round 5, R5-4 — sender's single read, LIVE-INHERITING from the
 * sequence rather than the rung's stale copy.
 *
 * Round 4 (U9/SN1) copied a sequence's `default_sender` onto `mapping.sender`
 * at map time and treated that copy as authoritative forever after — so a
 * sender edit made in Config never reached a rung that had already mapped
 * the sequence (R5-4's bug report). Sender is locked to the sequence
 * identity (SN1 — plays cannot override it), so the sequence's OWN current
 * value must win whenever it is known, full stop — there is deliberately NO
 * `sequenceSummary.sender ?? mapping.sender` merge once the sequence has
 * loaded. That merge would silently resurrect the per-rung override decision
 * 3 removed, and would make an intentional "clear the sender" edit in Config
 * unobservable to any rung that happened to have an old copied value.
 *
 * `sequenceSummary` is `undefined` only for the brief gap before its query
 * resolves (the library summary, or a sequence detail fetch) — in that
 * window only, this falls back to the rung's stale copy, purely so a mapped
 * rung's badge doesn't flash "no sender" for one render and then correct
 * itself. That is a loading-state accommodation, not a data fallback: the
 * instant `sequenceSummary` is known, its value is used even when it is
 * `null` (no sender set on the sequence) and the rung's copy still says
 * otherwise.
 */
export function effectiveSender(
  sequenceSummary: { sender: string | null } | undefined,
  mapping: Pick<SequenceMapping, 'sender'> | null,
): string {
  if (sequenceSummary !== undefined) return sequenceSummary.sender ?? '';
  return mapping?.sender ?? '';
}

export type SenderChoiceResult = { ok: true; sender: string } | { ok: false; error: string };

/**
 * The choice as a `mapping.sender` value, or the reason it isn't one yet.
 *
 * A result type rather than a throw: "the rep hasn't finished typing" is an
 * ordinary state of a form, not an exception — and the caller needs the reason
 * string to render inline next to the field.
 */
export function resolveSenderChoice(choice: SenderChoice): SenderChoiceResult {
  if (choice.option !== STATIC_EMAIL_SENDER_OPTION) {
    return { ok: true, sender: choice.option };
  }
  const trimmed = choice.email.trim();
  if (trimmed.length === 0) return { ok: false, error: 'Enter an email address.' };
  if (!isValidSenderEmail(trimmed)) return { ok: false, error: 'Enter a valid email address.' };
  return { ok: true, sender: trimmed };
}
